<script setup>
// Quem está atendendo a conversa: a IA ou um humano. Um switch de dois ícones
// no cabeçalho (🤖 / 👤), com o lado ativo aceso; o nome de quem assumiu vai
// no tooltip, e não no texto, porque nome comprido estourava o cabeçalho em
// tela estreita. Aparece só em conta que tem usuário marcado como IA (ou em
// conversa que já carrega o sinal). O clique chama o endpoint; o servidor
// grava o sinal, atribui e registra a atividade.
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const salvando = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);
const account = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const iaUserIds = computed(() => account.value?.ia_user_ids || []);

const atendimento = computed(() => props.chat?.custom_attributes?.atendimento);
const humano = computed(() => atendimento.value === 'humano');

// Quem está no painel é sempre gente, mesmo logado com o usuário da IA: o
// servidor só trata como IA quem fala pela API com token (ver o concern
// AtendimentoIa). Por isso o switch aparece pra todo mundo.
const visivel = computed(
  () => props.chat?.id && (iaUserIds.value.length > 0 || atendimento.value)
);

// Nome de quem assumiu: o responsável da conversa quando bate com o registro,
// senão a lista de agentes.
const nomeHumano = computed(() => {
  const por = props.chat?.custom_attributes?.atendimento_por;
  const assignee = props.chat?.meta?.assignee;
  if (assignee && assignee.id === por) return assignee.name;
  const agente = (store.getters['agents/getAgents'] || []).find(
    a => a.id === por
  );
  return agente?.name;
});

const tooltip = computed(() => {
  if (!humano.value) return t('CONVERSATION.HEADER.ATENDIMENTO.IA_ATENDENDO');
  return nomeHumano.value
    ? t('CONVERSATION.HEADER.ATENDIMENTO.HUMANO_ATENDENDO', {
        name: nomeHumano.value,
      })
    : t('CONVERSATION.HEADER.ATENDIMENTO.HUMANO_GENERICO');
});

const definir = async valor => {
  if (salvando.value) return;
  if ((valor === 'humano') === humano.value) return;
  salvando.value = true;
  try {
    const { data } = await ConversationApi.atendimento({
      conversationId: props.chat.id,
      atendimento: valor,
    });
    store.dispatch('updateConversation', data);
    useAlert(
      t(
        valor === 'humano'
          ? 'CONVERSATION.HEADER.ATENDIMENTO.ASSUMIDO'
          : 'CONVERSATION.HEADER.ATENDIMENTO.DEVOLVIDO'
      )
    );
  } catch {
    useAlert(t('CONVERSATION.HEADER.ATENDIMENTO.ERRO'));
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <div
    v-if="visivel"
    v-tooltip.bottom="tooltip"
    class="inline-flex items-center gap-1 p-1 rounded-full bg-n-alpha-2 border border-n-weak flex-shrink-0"
    :class="{ 'opacity-60': salvando }"
    role="group"
  >
    <button
      type="button"
      class="w-9 h-9 rounded-full flex items-center justify-center border-0 cursor-pointer transition-colors"
      :class="
        humano
          ? 'bg-transparent text-n-slate-10 hover:text-n-slate-12'
          : 'bg-n-teal-9 text-white'
      "
      :aria-label="$t('CONVERSATION.HEADER.ATENDIMENTO.DEVOLVER')"
      :aria-pressed="!humano"
      @click="definir('ia')"
    >
      <span class="i-lucide-bot w-5 h-5" />
    </button>
    <button
      type="button"
      class="w-9 h-9 rounded-full flex items-center justify-center border-0 cursor-pointer transition-colors"
      :class="
        humano
          ? 'bg-n-slate-12 text-n-slate-1'
          : 'bg-transparent text-n-slate-10 hover:text-n-slate-12'
      "
      :aria-label="$t('CONVERSATION.HEADER.ATENDIMENTO.ASSUMIR')"
      :aria-pressed="humano"
      @click="definir('humano')"
    >
      <span class="i-lucide-user w-5 h-5" />
    </button>
  </div>
</template>
